import { pipe, Option } from 'effect';

// not the UI language: amounts must read the same as in the AG Grid amount columns
const DEFAULT_LOCALE = 'en-US';

const FRACTION_DIGITS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

// `Intl` throws on a malformed currency code; the caller falls back to the plain number
const currencyFormatter = Option.liftThrowable(
  (locale: string, currency: string) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, ...FRACTION_DIGITS }),
);

const numberFormatter = (locale: string) => new Intl.NumberFormat(locale, FRACTION_DIGITS);

const toNumber = (value: string | number): Option.Option<number> =>
  pipe(Option.some(typeof value === 'number' ? value : Number(value.trim())), Option.filter(Number.isFinite));

/**
 * `$5,000.00` / `₪5,000.00`, or `5,000.00` without a valid currency code.
 * Empty input gives `''`; a non-numeric value is returned unchanged rather than shown as zero.
 */
export const formatAmount = (
  value: string | number | null | undefined,
  currencyCode?: string | null,
  locale: string = DEFAULT_LOCALE,
): string => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return '';

  return pipe(
    toNumber(value),
    Option.map((amount) =>
      pipe(
        Option.fromNullable(currencyCode),
        Option.map((code) => code.trim()),
        Option.filter((code) => code.length > 0),
        Option.flatMap((code) => currencyFormatter(locale, code)),
        Option.getOrElse(() => numberFormatter(locale)),
      ).format(amount),
    ),
    Option.getOrElse(() => String(value)),
  );
};
